/**
 * Safe-Mode controls (Policies & Safety) — the asymmetric brake, wired.
 *
 * Backend contract (backend/app/routes/agentic_config.py +
 * services/agentic/safe_mode.py, architect binding condition R6):
 *   ENTER  POST /agentic/safe-mode/enter {reason}
 *          single actor, immediate; permission agentic:config:propose.
 *   EXIT   is dual control in THREE audited steps, each a separate request:
 *          1. Request  POST /agentic/config-approvals
 *                      {change_type:"safe_mode_exit", reason}   (propose)
 *          2. Approve  POST /agentic/config-approvals/{id}/decide  by a
 *                      DIFFERENT human holding agentic:config:approve
 *                      (Config Approvals page — DecideApprovalDialog)
 *          3. Execute  POST /agentic/safe-mode/exit {approval_id}
 *                      consumes the APPROVED authorization (single-use,
 *                      24h validity, atomic approved→applied); permission
 *                      agentic:config:propose.
 * There is deliberately NO single "exit" button: the only exit control is
 * bound to a specific approved authorization row.
 *
 * Fail-safe UI: controls render only when the server state is KNOWN
 * (`active` / `inactive`). Unknown/loading offers nothing. Client gating is
 * UX only — every permission is enforced server-side.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, Field, Modal, Stack, Text, Textarea } from '@smms/design-system';
import {
  enterSafeMode,
  exitSafeMode,
  listConfigApprovals,
  proposeSafeModeExit,
  SAFE_MODE_ENTER_REFUSED_DETAIL,
  SAFE_MODE_EXIT_CHANGE_TYPE,
  type ConfigApproval,
  type SafeModeState,
} from '../api/agentic';
import { describeMutationError, type MutationErrorView } from './mutationError';
import type { SafeModeStatus } from './useSafeMode';

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ErrorBox({ error }: { error: MutationErrorView | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-md border border-danger/40 bg-danger/10 p-3">
      <p className="text-sm text-danger">{error.message}</p>
      {error.hint && <p className="mt-1 text-xs text-danger">{error.hint}</p>}
    </div>
  );
}

/** Reason-collecting dialog shared by ENTER and REQUEST-EXIT. */
function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmVariant,
  pending,
  error,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant: 'destructive' | 'primary';
  pending: boolean;
  error: MutationErrorView | null;
  onConfirm: (reason: string | null) => void;
}) {
  const [reason, setReason] = useState('');
  const close = (next: boolean) => {
    if (pending) return;
    if (!next) setReason('');
    onOpenChange(next);
  };
  return (
    <Modal open={open} onOpenChange={close} size="md" disableBackdropClose={pending}>
      <Modal.Header title={title} description={description} />
      <Modal.Body>
        <Stack gap="sm">
          <Field label="Reason" helper="Recorded in the hash-chained audit log with your identity.">
            <Textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={2000}
              disabled={pending}
            />
          </Field>
          <ErrorBox error={error} />
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => close(false)} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant={confirmVariant}
          onClick={() => onConfirm(reason.trim() ? reason.trim() : null)}
          disabled={pending}
          isLoading={pending}
          loadingLabel="Submitting"
        >
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export interface SafeModeControlsProps {
  status: SafeModeStatus;
  state: SafeModeState | undefined;
}

export default function SafeModeControls({ status, state }: SafeModeControlsProps) {
  const queryClient = useQueryClient();
  const [enterOpen, setEnterOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [executeTarget, setExecuteTarget] = useState<ConfigApproval | null>(null);
  const [enterError, setEnterError] = useState<MutationErrorView | null>(null);
  const [requestError, setRequestError] = useState<MutationErrorView | null>(null);
  const [executeError, setExecuteError] = useState<MutationErrorView | null>(null);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['agentic'] });

  // Exit authorizations only matter while the brake is on.
  const approvalsQ = useQuery({
    queryKey: ['agentic', 'config-approvals'],
    queryFn: () => listConfigApprovals(),
    enabled: status === 'active',
  });
  const exitRequests = (approvalsQ.data ?? []).filter(
    a => a.change_type === SAFE_MODE_EXIT_CHANGE_TYPE,
  );
  const pendingExits = exitRequests.filter(a => a.status === 'pending');
  const approvedExits = exitRequests.filter(a => a.status === 'approved');

  const enterM = useMutation({
    mutationFn: (reason: string | null) => enterSafeMode(reason),
    onSuccess: async () => {
      setEnterError(null);
      setEnterOpen(false);
      await invalidate();
    },
    onError: err => setEnterError(describeMutationError(err, 'agentic:config:propose')),
  });

  const requestM = useMutation({
    mutationFn: (reason: string | null) => proposeSafeModeExit(reason),
    onSuccess: async () => {
      setRequestError(null);
      setRequestOpen(false);
      setRequestSubmitted(true);
      await invalidate();
    },
    onError: err => setRequestError(describeMutationError(err, 'agentic:config:propose')),
  });

  const executeM = useMutation({
    mutationFn: (approvalId: string) => exitSafeMode(approvalId),
    onSuccess: async () => {
      setExecuteError(null);
      setExecuteTarget(null);
      setRequestSubmitted(false);
      await invalidate();
    },
    onError: err => {
      setExecuteError(describeMutationError(err, 'agentic:config:propose'));
      // Consumed / expired / no longer active — refresh what we show.
      void invalidate();
    },
  });

  if (status === 'loading' || status === 'not_entitled') return null;

  if (status === 'unknown') {
    return (
      <Text variant="body-sm" tone="muted">
        Safe Mode state could not be determined — controls are disabled until it loads.
      </Text>
    );
  }

  if (status === 'inactive') {
    // F4: the brake must be releasable. The server refuses ENTER (409) when
    // no OTHER active user holds agentic:config:approve; mirror that here so
    // the operator sees why before they try. UX only — the server enforces.
    // Row 1121: same for propose — exiting also needs an ACTIVE propose
    // holder to REQUEST/EXECUTE it later. Unlike the approve check above,
    // the backend's propose count is NOT caller-exclusive (no self-decide
    // rule on the propose side) — the caller themselves counts here.
    const canEnter =
      state?.exit_checker_available === true && state?.propose_checker_available === true;
    return (
      <>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="destructive"
            onClick={() => setEnterOpen(true)}
            disabled={!canEnter}
            aria-describedby={canEnter ? undefined : 'safe-mode-enter-refused'}
          >
            Enter Safe Mode
          </Button>
          {canEnter ? (
            <Text variant="caption" tone="muted">
              Halts agent actions immediately. Single actor; exiting later needs a second approver.
            </Text>
          ) : (
            <p id="safe-mode-enter-refused" role="status" className="text-sm text-danger">
              {SAFE_MODE_ENTER_REFUSED_DETAIL}
            </p>
          )}
        </div>
        <ReasonDialog
          open={enterOpen}
          onOpenChange={next => {
            setEnterOpen(next);
            if (!next) setEnterError(null);
          }}
          title="Enter Safe Mode"
          description="This is the emergency brake: agent actions for your tenant are halted the moment you confirm. No second approval is needed to enter; exiting requires one."
          confirmLabel="Enter Safe Mode now"
          confirmVariant="destructive"
          pending={enterM.isPending}
          error={enterError}
          onConfirm={reason => enterM.mutate(reason)}
        />
      </>
    );
  }

  // status === 'active'
  return (
    <Stack gap="sm">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" onClick={() => setRequestOpen(true)}>
          Request Safe Mode exit
        </Button>
        <Text variant="caption" tone="muted">
          Dual control: your request must be approved by a different person in Config Approvals,
          then executed here.
        </Text>
      </div>

      {state?.exit_possible === false && (
        // Row 1121 (adversarial review round 2, F3): the banner fires on
        // the viewer-independent exit_possible flag, not on
        // exit_checker_available/propose_checker_available individually —
        // those two can BOTH read true for a tenant that is actually
        // stuck (the only person who holds either permission is the same
        // single person, so the self-decide rule blocks them from ever
        // being their own second approver). Covers both failure shapes:
        // nobody holds propose at all, or the propose- and approve-holder
        // sets collapse to one person.
        <p role="alert" className="text-sm text-danger">
          No two different active administrators currently hold the propose and approve
          permissions needed to request, decide, and execute a Safe-Mode exit — one person
          cannot approve their own request. Grant agentic:config:propose or
          agentic:config:approve to a second active administrator before attempting to exit.
        </p>
      )}

      {requestSubmitted && (
        <p role="status" className="text-sm text-text">
          Exit request submitted — a different approver must approve it in Config Approvals before
          it can be executed.
        </p>
      )}

      {pendingExits.length > 0 && (
        <Text variant="body-sm" tone="muted">
          {pendingExits.length} exit request{pendingExits.length === 1 ? '' : 's'} awaiting a second
          approver.
        </Text>
      )}

      {approvedExits.length > 0 && (
        <Card padding="none">
          <ul className="divide-y divide-border" aria-label="Approved exit authorizations">
            {approvedExits.map(a => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Badge variant="success" size="sm">
                  approved
                </Badge>
                <div className="min-w-0 flex-1">
                  <Text variant="body-sm" className="font-medium">
                    Exit authorization {a.id.slice(0, 8)}
                  </Text>
                  <Text variant="caption" tone="muted">
                    Approved {formatWhen(a.decided_at)} · valid 24h · single use
                  </Text>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setExecuteError(null);
                    setExecuteTarget(a);
                  }}
                >
                  Execute exit
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ReasonDialog
        open={requestOpen}
        onOpenChange={next => {
          setRequestOpen(next);
          if (!next) setRequestError(null);
        }}
        title="Request Safe Mode exit"
        description="Creates a dual-control request. It does not exit Safe Mode: a different person with approve rights must approve it, and the exit is then executed as a separate audited step."
        confirmLabel="Submit exit request"
        confirmVariant="primary"
        pending={requestM.isPending}
        error={requestError}
        onConfirm={reason => requestM.mutate(reason)}
      />

      <Modal
        open={executeTarget !== null}
        onOpenChange={next => {
          if (executeM.isPending) return;
          if (!next) {
            setExecuteTarget(null);
            setExecuteError(null);
          }
        }}
        size="sm"
        role="alertdialog"
        disableBackdropClose={executeM.isPending}
      >
        <Modal.Header
          title="Exit Safe Mode"
          description="Consumes the approved authorization (single use) and resumes agent operation for your tenant."
        />
        <Modal.Body>
          <Stack gap="sm">
            {executeTarget && (
              <Text variant="body-sm" tone="muted">
                Authorization {executeTarget.id} — approved {formatWhen(executeTarget.decided_at)}
                {state?.entered_at ? `. Safe Mode was entered ${formatWhen(state.entered_at)}` : ''}
                {state?.reason ? ` (${state.reason})` : ''}.
              </Text>
            )}
            <ErrorBox error={executeError} />
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setExecuteTarget(null);
              setExecuteError(null);
            }}
            disabled={executeM.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => executeTarget && executeM.mutate(executeTarget.id)}
            disabled={executeM.isPending}
            isLoading={executeM.isPending}
            loadingLabel="Exiting"
          >
            Exit Safe Mode now
          </Button>
        </Modal.Footer>
      </Modal>
    </Stack>
  );
}
